import { Message } from '@ipfabric/ui-components/Message/Message';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { Button } from 'components/Button/Button';

import { FormLayout } from 'components/FormLayout/FormLayout';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, useFormik } from 'formik';
import { validateUserPassword } from 'pages/Settings/Administration/LocalUsers/helpers/validateUserPassword';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { NewPasswordRequirementDescription } from './NewPasswordRequirementDescription';

export interface PasswordFormValues {
  oldPassword: string;
  newPassword: string;
  retypePassword: string;
}

type Props = {
  className?: string;
};

export const CurrentUserPasswordForm: FC<Props> = (props) => {
  const msg = useLocale();
  const passwordFormMsg = msg.userProfile.passwordForm;
  const validationsMsg = msg.localUsers.form.validations;
  const [submitting, setSubmitting] = useLoadingState();
  const api = useApi();
  const notifications = useNotifications();
  const navigate = useNavigate();

  const handleSubmit = async (values: PasswordFormValues) => {
    await withLoadingIndicator(setSubmitting)(async () => {
      await api.request({
        url: '/users/me/password',
        method: 'POST',
        data: {
          newPassword: values.newPassword,
          oldPassword: values.oldPassword,
        },
      });
      notifications.showNotification({
        type: 'success',
        message: passwordFormMsg.updated,
      });
      navigate('/logout');
    });
  };

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      retypePassword: '',
    },
    validate: (values: PasswordFormValues) => {
      const errors: FormikErrors<PasswordFormValues> = {};
      if (values.oldPassword === '') {
        errors.oldPassword = validationsMsg.required;
      }
      validateUserPassword(values, errors, validationsMsg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit(handleSubmit),
  });

  const canSubmit =
    formik.values.oldPassword !== '' &&
    formik.values.newPassword !== '' &&
    formik.values.retypePassword !== '';

  return (
    <form onSubmit={formik.handleSubmit} className={props.className}>
      <FormLayout width='full' noPadding>
        <header>
          <h2>{passwordFormMsg.title}</h2>
        </header>
        <Message
          className='mb max-w-96'
          variant='plain'
          type='info'
          message={passwordFormMsg.passwordChangeInfo}
        />
        <FormikTextField
          className='max-w-96'
          name='oldPassword'
          formik={formik}
          type='password'
          label={passwordFormMsg.oldPassword}
        />
        <FormikTextField
          className='max-w-96'
          name='newPassword'
          formik={formik}
          type='password'
          label={passwordFormMsg.newPassword}
          description={<NewPasswordRequirementDescription />}
        />
        <FormikTextField
          className='max-w-96'
          name='retypePassword'
          formik={formik}
          type='password'
          label={passwordFormMsg.retypePassword}
        />
        <Button
          disabled={!canSubmit}
          loading={submitting === 'LOADING'}
          type='submit'
          variant='primary'
          size='sm'
          icon='fa-floppy-disk'
        >
          {passwordFormMsg.save}
        </Button>
      </FormLayout>
    </form>
  );
};
